import { useState, useMemo, useEffect, useRef } from 'react';
import { VizData as BarChartVizData } from './EnhancedBarChart.types';

// Extended VizData type that includes Area chart type
type VizData = Omit<BarChartVizData, 'chart_type'> & {
    chart_type: 'Bar' | 'Line' | 'Area';
};

// Metric identifier: 'familyIndex_metricIndex'
export type MetricId = string;

export interface ChartFilters {
    x: string[];
    y1: MetricId[]; // Selected metrics for y1 (format: 'familyIndex_metricIndex')
    y2: MetricId[]; // Selected metrics for y2 (format: 'familyIndex_metricIndex')
    z: string[][]; // Array of arrays: [z1 selections, z2 selections, z3 selections, ...]
}

/**
 * Hook to manage chart filters for viz_data format
 */
export function useChartFilters(vizData: VizData | undefined) {
    const [filters, setFilters] = useState<ChartFilters>({
        x: [],
        y1: [],
        y2: [],
        z: [], // Will be initialized based on zAxisTitle length
    });

    // Track if y1 has been initialized for the current vizData to prevent re-initialization when user clears it
    const initializedVizDataRef = useRef<VizData | undefined>(undefined);

    // Auto-initialize y1 with first metric only once when vizData first becomes available
    // After that, allow user to clear y1 without re-initialization
    // Reset initialization tracking when vizData changes to a new dataset
    useEffect(() => {
        // Guard: loading placeholders (e.g. { _loading: true, chart_type: 'BarChart' }) have no data
        if (!vizData || !Array.isArray(vizData.data)) return;
        // Check if this is a new vizData (different reference or first time)
        const isNewVizData = vizData !== initializedVizDataRef.current;
        
        if (isNewVizData) {
            // Use functional form to read current filter state
            setFilters((prev) => {
                const updates: Partial<ChartFilters> = {};
                
                // Initialize y1 if empty
                if (vizData.yAxisTitle?.length > 0 && prev.y1.length === 0 && prev.y2.length === 0) {
                    const firstFamily = vizData.yAxisTitle[0];
                    if (firstFamily && firstFamily.length > 0) {
                        updates.y1 = ['0_0']; // Default to first metric in first family
                    }
                }
                
                // Initialize z filters array based on zAxisTitle length
                // Default to all options at each Z level so every z dimension is enabled on initial load
                if (vizData.zAxisTitle && vizData.zAxisTitle.length > 0) {
                    const zLevels = vizData.zAxisTitle.length;
                    if (prev.z.length !== zLevels || prev.z.every((levelSel) => levelSel.length === 0)) {
                        const defaultZ: string[][] = [];
                        for (let level = 0; level < zLevels; level++) {
                            const levelValues = Array.from(
                                new Set(
                                    vizData.data
                                        .map((d) => d.z?.[level])
                                        .filter((val): val is string => val !== undefined && val !== null)
                                )
                            ).sort();
                            defaultZ.push(levelValues);
                        }
                        updates.z = defaultZ;
                    }
                } else if (prev.z.length > 0) {
                    updates.z = [];
                }
                
                if (Object.keys(updates).length > 0) {
                    initializedVizDataRef.current = vizData;
                    return { ...prev, ...updates };
                }
                return prev;
            });
        }
    }, [vizData]);

    // Get available filter options
    const filterOptions = useMemo(() => {
        if (!vizData || !Array.isArray(vizData.data)) return null;

        // X-axis options (unique x values) - guard d.x for table-like rows (array) vs chart objects
        const xOptions = Array.from(
            new Set(vizData.data.flatMap((d) => (Array.isArray(d?.x) ? d.x : d?.x != null ? [d.x] : [])))
        ).sort();

        // Y-axis options (individual metrics across all families)
        const yOptions: Array<{ label: string; value: MetricId; familyIndex: number; metricIndex: number }> = [];
        (vizData.yAxisTitle ?? []).forEach((family, familyIndex) => {
            (Array.isArray(family) ? family : []).forEach((metricLabel, metricIndex) => {
                yOptions.push({
                    label: metricLabel,
                    value: `${familyIndex}_${metricIndex}`,
                    familyIndex,
                    metricIndex,
                });
            });
        });

        // Z-axis options - array of arrays, one per Z level
        // Each level contains unique values for that Z dimension
        const zOptions: string[][] = [];
        if (vizData.zAxisTitle && vizData.zAxisTitle.length > 0) {
            for (let level = 0; level < vizData.zAxisTitle.length; level++) {
                const levelValues = Array.from(
                    new Set(
                        vizData.data
                            .map((d) => d.z?.[level])
                            .filter((val): val is string => val !== undefined && val !== null)
                    )
                ).sort();
                zOptions.push(levelValues);
            }
        }

        return {
            x: xOptions,
            y: yOptions,
            z: zOptions, // Array of arrays: [z1 options, z2 options, ...]
        };
    }, [vizData]);

    // Update filter
    const updateFilter = (type: keyof ChartFilters, value: any, zLevel?: number) => {
        setFilters((prev) => {
            if (type === 'x') {
                // For x, value is an array of selected values
                return {
                    ...prev,
                    [type]: Array.isArray(value) ? value : [value],
                };
            } else if (type === 'z' && zLevel !== undefined) {
                // For z, update specific level
                const newZ = [...prev.z];
                newZ[zLevel] = Array.isArray(value) ? value : [value];
                
                // Clear all lower level filters when a higher level is cleared
                // If user clears Z1, clear Z2, Z3, etc.
                if (newZ[zLevel].length === 0) {
                    for (let i = zLevel + 1; i < newZ.length; i++) {
                        newZ[i] = [];
                    }
                }
                
                return {
                    ...prev,
                    z: newZ,
                };
            } else if (type === 'y1') {
                // For y1, value is an array of selected metrics
                const selectedMetrics = Array.isArray(value) ? value : [];
                // Remove metrics from y2 that belong to the same families as selected y1 metrics
                const y1FamilyIndices = new Set(selectedMetrics.map((m: MetricId) => Number(m.split('_')[0])));
                const filteredY2 = prev.y2.filter((m: MetricId) => !y1FamilyIndices.has(Number(m.split('_')[0])));
                return {
                    ...prev,
                    y1: selectedMetrics,
                    y2: filteredY2,
                };
            } else if (type === 'y2') {
                // For y2, value is an array of selected metrics
                const selectedMetrics = Array.isArray(value) ? value : [];
                // Remove metrics from y1 that belong to the same families as selected y2 metrics
                const y2FamilyIndices = new Set(selectedMetrics.map((m: MetricId) => Number(m.split('_')[0])));
                const filteredY1 = prev.y1.filter((m: MetricId) => !y2FamilyIndices.has(Number(m.split('_')[0])));
                return {
                    ...prev,
                    y2: selectedMetrics,
                    y1: filteredY1,
                };
            }
            return prev;
        });
    };

    // Get filtered data
    const filteredData = useMemo(() => {
        if (!vizData || !Array.isArray(vizData.data)) return [];

        let filtered = vizData.data;

        // Filter by x-axis
        if (filters.x.length > 0) {
            filtered = filtered.filter((dataPoint) => {
                const xValue = dataPoint.x[0];
                return filters.x.includes(xValue);
            });
        }

        // Filter by z-axis - require selection of lowest Z level (last/highest index)
        if (vizData.zAxisTitle && vizData.zAxisTitle.length > 0 && filters.z.length > 0) {
            const totalZLevels = vizData.zAxisTitle.length;
            const lowestZLevelIndex = totalZLevels - 1; // Z1=0, Z2=1, etc. Lowest is the last one
            
            // Check if the lowest Z level is selected
            const lowestLevelSelections = filters.z[lowestZLevelIndex] || [];
            
            if (lowestLevelSelections.length > 0) {
                // User has selected the lowest level - filter data by all Z levels
                filtered = filtered.filter((dataPoint) => {
                    if (!dataPoint.z || dataPoint.z.length === 0) return false;
                    
                    // Check all Z levels - all must match
                    for (let level = 0; level < totalZLevels; level++) {
                        const selectedValues = filters.z[level] || [];
                        if (selectedValues.length === 0) {
                            // If any level is not selected, don't show data
                            return false;
                        }
                        const dataPointZValue = dataPoint.z[level];
                        if (!selectedValues.includes(dataPointZValue)) {
                            return false;
                        }
                    }
                    return true;
                });
            } else {
                // If lowest Z level is not selected, show no data
                // User must select the lowest Z level to see data
                filtered = [];
            }
        }

        return filtered;
    }, [vizData, filters.x, filters.z]);

    // Get selected metrics for y1 and y2
    const selectedMetrics = useMemo(() => {
        return {
            y1: filters.y1,
            y2: filters.y2,
        };
    }, [filters.y1, filters.y2]);

    // Get available Z options for each level based on parent selections
    // This enables hierarchical filtering where Z2 options depend on selected Z1 values
    const zFilterOptions = useMemo(() => {
        if (!vizData || !vizData.zAxisTitle || vizData.zAxisTitle.length === 0) {
            return [];
        }

        const zLevels = vizData.zAxisTitle.length;
        const availableOptions: Array<{ label: string; value: string; disabled: boolean }[]> = [];

        for (let level = 0; level < zLevels; level++) {
            const levelOptions: Array<{ label: string; value: string; disabled: boolean }> = [];
            
            if (level === 0) {
                // Z1 level: show all available values
                const z1Values = filterOptions?.z[0] || [];
                z1Values.forEach((value) => {
                    levelOptions.push({
                        label: value,
                        value: value,
                        disabled: false,
                    });
                });
            } else {
                // Z2, Z3, etc.: only show values whose parents are selected
                const parentLevel = level - 1;
                const parentSelections = filters.z[parentLevel] || [];
                
                if (parentSelections.length === 0) {
                    // If no parent is selected, disable all options
                    const levelValues = filterOptions?.z[level] || [];
                    levelValues.forEach((value) => {
                        levelOptions.push({
                            label: value,
                            value: value,
                            disabled: true,
                        });
                    });
                } else {
                    // Find all data points that match immediate parent selection
                    // For Z2, check if Z1 matches; for Z3, check if Z2 matches, etc.
                    const matchingDataPoints = vizData.data.filter((dataPoint) => {
                        if (!dataPoint.z || dataPoint.z.length <= parentLevel) return false;
                        
                        // Check only the immediate parent level
                        const parentSelectionsForLevel = filters.z[parentLevel] || [];
                        if (parentSelectionsForLevel.length === 0) return false;
                        return parentSelectionsForLevel.includes(dataPoint.z[parentLevel]);
                    });
                    
                    // Get unique values for this level from matching data points
                    const availableValues = Array.from(
                        new Set(
                            matchingDataPoints
                                .map((d) => d.z?.[level])
                                .filter((val): val is string => val !== undefined && val !== null)
                        )
                    ).sort();
                    
                    // Get all possible values for this level
                    const allLevelValues = filterOptions?.z[level] || [];
                    
                    allLevelValues.forEach((value) => {
                        const isAvailable = availableValues.includes(value);
                        levelOptions.push({
                            label: value,
                            value: value,
                            disabled: !isAvailable,
                        });
                    });
                }
            }
            
            availableOptions.push(levelOptions);
        }

        return availableOptions;
    }, [vizData, filterOptions, filters.z]);

    return {
        filters,
        filterOptions,
        filteredData,
        selectedMetrics,
        updateFilter,
        zFilterOptions, // Hierarchical Z filter options with disabled states
    };
}
